import * as React from "react"

import { cn } from "@/lib/utils"
import {
  applySanitizedValueToChangeEvent,
  sanitizePlainTextInput,
} from "@/lib/sanitize-input"

type TextareaProps = React.ComponentProps<"textarea"> & {
  /** Sanitize user input on change. Defaults to true. */
  sanitize?: boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onChange, sanitize = true, maxLength, ...props }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!onChange) {
        return
      }

      if (!sanitize) {
        onChange(event)
        return
      }

      const sanitizedValue = sanitizePlainTextInput(event.target.value, {
        allowNewlines: true,
        stripHtml: true,
        maxLength: maxLength ?? undefined,
      })

      onChange(applySanitizedValueToChangeEvent(event, sanitizedValue))
    }

    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        maxLength={maxLength}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-background border-input flex w-full min-w-0 rounded-lg border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none resize-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 ",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className
        )}
        onChange={handleChange}
        {...props}
      />
    )
  }
)

Textarea.displayName = "Textarea"

export { Textarea }
